/**
 * EmailTemplateEditor — tab for managing email templates and sending emails.
 * Enhanced version: creates email documents in Firestore (resume-emails collection)
 * with status='pending'. A cloud function picks them up for delivery via SendGrid.
 */

import { withFeedback } from '../lib/buttonFeedback.js';

const DISPOSITIONS = [
    ['', '— Any —'],
    ['contact-1st', 'Contact for 1st Interview'],
    ['waiting-1st', 'Waiting for 1st Interview'],
    ['downselect-1st', '1st Interview Down-select'],
    ['contact-2nd', 'Contact for 2nd Interview'],
    ['waiting-2nd', 'Waiting for 2nd Interview'],
    ['downselect-2nd', '2nd Interview Down-select'],
    ['invite-tour', 'Invite for Tour'],
    ['waiting-tour', 'Waiting for Tour'],
    ['waiting-no', 'Waiting to Send No'],
    ['downselect-hire', 'Down-select for Hire'],
    ['hold', 'Hold'],
    ['no', 'No'],
];

const PLACEHOLDERS = [
    ['{{firstName}}', "Candidate's first name"],
    ['{{name}}', 'Full name'],
    ['{{email}}', 'Email address'],
    ['{{interviewDate}}', 'Suggested interview date/time'],
    ['{{education}}', 'Education level'],
    ['{{skills}}', 'Skill summary'],
    ['{{career}}', 'Career summary'],
];

export class EmailTemplateEditor {
    constructor(options = {}) {
        this._templateStore = options.templateStore;
        this._candidateStore = options.candidateStore;
        this._projectStore = options.projectStore;
        this._db = options.db;
        this._statusBar = options.statusBar;
        this._userEmail = options.userEmail || '';
        this._projectId = options.projectId || null;
        this._selectedId = null;
        this._templates = [];
        this._el = document.createElement('div');
        this._el.className = 'rm-prompt-editor';
    }

    get element() {
        // Render initial state immediately so the tab isn't blank
        if (!this._rendered) {
            this._rendered = true;
            this.render();
        }
        return this._el;
    }

    setProjectId(projectId) {
        this._projectId = projectId;
    }

    setTemplates(templates) {
        this._templates = templates || [];
        this.render();
    }

    render() {
        this._el.innerHTML = '';

        // Top bar
        const topBar = document.createElement('div');
        topBar.style.cssText = 'display:flex;justify-content:space-between;align-items:center;';
        topBar.innerHTML = '<h3 style="margin:0;color:var(--text);">Email Templates</h3>';
        const addBtn = document.createElement('button');
        addBtn.className = 'rm-btn rm-btn-primary';
        addBtn.textContent = '+ New Template';
        addBtn.addEventListener('click', () => this._createNew());
        topBar.appendChild(addBtn);
        this._el.appendChild(topBar);

        // Split layout
        const split = document.createElement('div');
        split.style.cssText = 'display:flex;gap:16px;flex:1;min-height:0;';

        // Left: list
        const listPanel = document.createElement('div');
        listPanel.style.cssText = 'width:280px;flex-shrink:0;overflow-y:auto;display:flex;flex-direction:column;gap:8px;';

        for (const t of this._templates) {
            const card = document.createElement('div');
            card.className = `rm-prompt-card${t.id === this._selectedId ? ' active' : ''}`;
            const disp = DISPOSITIONS.find(d => d[0] === t.targetDisposition);
            card.innerHTML = `
                <div class="rm-prompt-card-header">
                    <span class="rm-prompt-card-name">${this._esc(t.name)}</span>
                </div>
                <div class="rm-prompt-card-desc">${this._esc(t.subject)}</div>
                <div style="font-size:11px;color:var(--text-muted);margin-top:4px;">
                    Target: ${disp ? disp[1] : 'Any'} · CC: ${(t.ccList || []).length}
                </div>
            `;
            card.addEventListener('click', () => { this._selectedId = t.id; this.render(); });
            listPanel.appendChild(card);
        }

        if (this._templates.length === 0) {
            listPanel.innerHTML = '<div class="rm-empty-state" style="padding:20px;"><div>No templates yet</div></div>';
        }

        split.appendChild(listPanel);

        // Right: form
        const formPanel = document.createElement('div');
        formPanel.style.cssText = 'flex:1;min-width:0;overflow-y:auto;';

        const selected = this._templates.find(t => t.id === this._selectedId);
        if (selected) {
            formPanel.appendChild(this._buildForm(selected));
        } else {
            formPanel.innerHTML = '<div class="rm-empty-state"><div>Select a template to edit, or create a new one</div></div>';
        }

        split.appendChild(formPanel);
        this._el.appendChild(split);
    }

    _buildForm(template) {
        const form = document.createElement('div');
        form.className = 'rm-prompt-form';

        form.innerHTML = `
            <div class="rm-form-row">
                <label>Template Name</label>
                <input type="text" id="rmEName" value="${this._esc(template.name)}">
            </div>
            <div class="rm-form-row">
                <label>Target Disposition</label>
                <select id="rmEDisposition" class="rm-disposition-select" style="width:100%;padding:8px 12px;font-size:14px;">
                    ${DISPOSITIONS.map(([val, label]) =>
                        `<option value="${val}" ${template.targetDisposition === val ? 'selected' : ''}>${label}</option>`
                    ).join('')}
                </select>
                <div style="font-size:11px;color:var(--text-muted);margin-top:4px;">
                    Only candidates with this disposition will receive the email.
                </div>
            </div>
            <div class="rm-form-row">
                <label>CC Emails (one per line)</label>
                <textarea id="rmECc" style="min-height:60px;">${this._esc((template.ccList || []).join('\n'))}</textarea>
            </div>
            <div class="rm-form-row">
                <label>Subject</label>
                <input type="text" id="rmESubject" value="${this._esc(template.subject)}">
            </div>
            <div class="rm-form-row">
                <label>Body</label>
                <textarea id="rmEBody" style="min-height:180px;">${this._esc(template.body)}</textarea>
                <div style="font-size:11px;color:var(--text-muted);margin-top:4px;">
                    Placeholders: ${PLACEHOLDERS.map(([p, d]) => `<code>${p}</code> (${d})`).join(', ')}
                </div>
            </div>
            <div style="display:flex;gap:8px;justify-content:space-between;margin-top:8px;">
                <button class="rm-btn rm-btn-danger" id="rmEDelete">Delete</button>
                <div style="display:flex;gap:8px;">
                    <button class="rm-btn" id="rmESave">Save</button>
                    <button class="rm-btn" id="rmECalendar">Send Calendar Invites</button>
                    <button class="rm-btn rm-btn-primary" id="rmESend">Save + Send Emails</button>
                </div>
            </div>
        `;

        const saveBtn = form.querySelector('#rmESave');
        const sendBtn = form.querySelector('#rmESend');
        const calBtn = form.querySelector('#rmECalendar');
        const deleteBtn = form.querySelector('#rmEDelete');
        saveBtn.addEventListener('click', () => withFeedback(saveBtn, () => this._save(template.id, form), { successText: '\u2713 Saved' }));
        sendBtn.addEventListener('click', () => withFeedback(sendBtn, async () => {
            await this._save(template.id, form);
            await this._sendEmails(template.id);
        }, { successText: '\u2713 Emails Queued' }));
        calBtn.addEventListener('click', () => withFeedback(calBtn, () => this._sendCalendarInvites(template.id), { successText: '\u2713 Invites Sent' }));
        deleteBtn.addEventListener('click', () => withFeedback(deleteBtn, () => this._delete(template.id), { successText: '\u2713 Deleted' }));

        return form;
    }

    _getFormData(form) {
        return {
            name: form.querySelector('#rmEName').value.trim(),
            subject: form.querySelector('#rmESubject').value.trim(),
            body: form.querySelector('#rmEBody').value.trim(),
            ccList: form.querySelector('#rmECc').value.trim().split('\n').map(e => e.trim()).filter(Boolean),
            targetDisposition: form.querySelector('#rmEDisposition').value,
        };
    }

    async _save(id, form) {
        const data = this._getFormData(form);
        await this._templateStore.update(id, data);
        if (this._statusBar) this._statusBar.log('Template saved', 'success');
    }

    async _delete(id) {
        if (!confirm('Delete this email template?')) return;
        await this._templateStore.remove(id);
        this._selectedId = null;
        if (this._statusBar) this._statusBar.log('Template deleted', 'info');
    }

    async _createNew() {
        const id = await this._templateStore.create({
            name: 'New Email Template',
            subject: 'Interview Opportunity - Official Pest Prevention / Raven',
            body: 'Dear {{firstName}},\n\nThank you for submitting your resume to Official Pest Prevention. We would like to schedule a meeting to discuss the opportunity further.\n\nAre you available on {{interviewDate}} for a video meeting?\n\nThank You,\nThe Raven Team',
            ccList: [],
            targetDisposition: 'contact-1st',
        }, this._userEmail);
        this._selectedId = id;
    }

    async _sendEmails(templateId) {
        const template = this._templateStore.getById(templateId);
        if (!template) return;

        const candidates = this._candidateStore.getAll();
        if (candidates.length === 0) {
            alert('No candidates in this project. Import resumes on the Project tab first.');
            return;
        }

        // Filter by disposition (skip check when "Any" is selected)
        let pool = candidates;
        let skippedDisposition = 0;
        if (template.targetDisposition) {
            pool = candidates.filter(c => c.disposition === template.targetDisposition);
            skippedDisposition = candidates.length - pool.length;
        }

        // Filter by email
        const noEmail = pool.filter(c => !c.email);
        const targets = pool.filter(c => !!c.email);

        if (targets.length === 0) {
            const dispLabel = template.targetDisposition
                ? DISPOSITIONS.find(d => d[0] === template.targetDisposition)?.[1] || template.targetDisposition
                : 'Any';
            const parts = [`${candidates.length} candidate(s) in this project`];
            if (skippedDisposition > 0) parts.push(`${skippedDisposition} don't match the target disposition "${dispLabel}"`);
            if (noEmail.length > 0) parts.push(`${noEmail.length} have no email address`);
            alert(`No candidates qualify for this email.\n\n${parts.join('\n')}\n\nTo send emails, set candidate dispositions on the Candidates tab to match the template's target disposition.`);
            return;
        }

        // Confirm before sending
        if (!confirm(`This will queue ${targets.length} email(s) for delivery. Continue?`)) return;

        // Get project settings for CC emails and campaign email
        let projectCcEmails = [];
        let campaignEmail = 'hiring@ravenresume.com';
        if (this._projectId && this._projectStore) {
            const project = this._projectStore.getById(this._projectId);
            if (project?.campaignEmail) campaignEmail = project.campaignEmail;
            if (project && project.ccEmails) {
                projectCcEmails = project.ccEmails;
            }
        }

        // Merge CC lists (template + project), deduplicate
        const allCc = [...new Set([...(template.ccList || []), ...projectCcEmails])];

        const db = this._db;
        if (!db) {
            if (this._statusBar) this._statusBar.log('Database not available', 'error');
            return;
        }

        let queued = 0;
        let failed = 0;

        for (const candidate of targets) {
            try {
                const subject = this._interpolate(template.subject, candidate);
                const body = this._interpolate(template.body, candidate);

                await db.collection('resume-emails').add({
                    status: 'pending',
                    direction: 'outbound',
                    to: candidate.email,
                    cc: allCc,
                    from: campaignEmail,
                    fromName: 'Raven Resume',
                    replyTo: this._userEmail,
                    subject,
                    body,
                    candidateId: candidate.id,
                    candidateName: candidate.name || candidate.fileName || '',
                    projectId: this._projectId || '',
                    templateId: templateId,
                    templateName: template.name || '',
                    sendgridMessageId: null,
                    sendgridEvents: [],
                    calendarAccepted: false,
                    error: null,
                    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                    sentAt: null,
                    createdBy: this._userEmail,
                });
                queued++;
            } catch (err) {
                console.error(`Failed to queue email for ${candidate.email}:`, err);
                failed++;
            }
        }

        if (this._statusBar) {
            if (failed === 0) {
                this._statusBar.log(`Queued ${queued} email(s) for delivery`, 'success');
            } else {
                this._statusBar.log(`Queued ${queued} email(s), ${failed} failed to queue`, 'warning');
            }
        }
        if (queued === 0 && failed > 0) throw new Error(`All ${failed} email(s) failed to queue`);
    }

    _interpolate(text, candidate) {
        const firstName = (candidate.name || '').split(' ')[0] || candidate.name || '';
        let interviewDate = '';
        if (candidate.interviewDate) {
            const d = candidate.interviewDate.toDate
                ? candidate.interviewDate.toDate()
                : new Date(candidate.interviewDate);
            if (!isNaN(d)) {
                interviewDate = d.toLocaleString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                });
            }
        }

        return text
            .replace(/\{\{firstName\}\}/g, firstName)
            .replace(/\{\{name\}\}/g, candidate.name || '')
            .replace(/\{\{email\}\}/g, candidate.email || '')
            .replace(/\{\{interviewDate\}\}/g, interviewDate)
            .replace(/\{\{education\}\}/g, candidate.education || '')
            .replace(/\{\{skills\}\}/g, candidate.skillSummary || '')
            .replace(/\{\{career\}\}/g, candidate.careerSummary || '');
    }

    async _sendCalendarInvites(templateId) {
        const template = this._templateStore.getById(templateId);
        if (!template) return;

        const candidates = this._candidateStore.getAll();
        if (candidates.length === 0) {
            alert('No candidates in this project.');
            return;
        }

        // Filter: must have disposition match (if set), email, AND interviewDate
        let pool = candidates;
        if (template.targetDisposition) {
            pool = pool.filter(c => c.disposition === template.targetDisposition);
        }

        const targets = pool.filter(c => c.email && c.interviewDate);

        if (targets.length === 0) {
            const withDate = pool.filter(c => c.interviewDate).length;
            const withEmail = pool.filter(c => c.email).length;
            const parts = [`${pool.length} candidate(s) match the disposition`];
            if (withDate === 0) parts.push('none have a "Suggested Interview" date set');
            else if (withEmail === 0) parts.push('none have an email address');
            else parts.push(`${withDate} have dates, ${withEmail} have emails, but ${targets.length} have both`);
            alert(`No candidates qualify for calendar invites.\n\n${parts.join('\n')}\n\nSet interview dates on the Candidates tab first.`);
            return;
        }

        // Get project calendar invitees
        let calendarInvitees = [];
        if (this._projectId && this._projectStore) {
            const project = this._projectStore.getById(this._projectId);
            if (project && project.calendarInvitees) {
                calendarInvitees = project.calendarInvitees;
            }
        }

        const names = targets.map(c => `  - ${c.name || c.email}`).join('\n');
        const inviteeList = calendarInvitees.length > 0
            ? `\n\nCalendar invitees (from project settings):\n  ${calendarInvitees.join(', ')}`
            : '';

        if (!confirm(`Send calendar invites with Google Meet to ${targets.length} candidate(s):\n\n${names}${inviteeList}\n\nContinue?`)) return;

        const db = this._db;
        if (!db) {
            alert('Database not available');
            return;
        }

        let queued = 0;
        let failed = 0;

        for (const candidate of targets) {
            try {
                const interviewDate = candidate.interviewDate?.toDate
                    ? candidate.interviewDate.toDate()
                    : new Date(candidate.interviewDate);

                if (isNaN(interviewDate)) {
                    failed++;
                    continue;
                }

                await db.collection('resume-emails').add({
                    type: 'calendar-invite',
                    status: 'pending',
                    direction: 'outbound',
                    to: candidate.email,
                    candidateName: candidate.name || '',
                    candidateId: candidate.id,
                    projectId: this._projectId || '',
                    calendarInvitees: calendarInvitees,
                    interviewDate: interviewDate.toISOString(),
                    durationMinutes: 30,
                    subject: `Interview: ${candidate.name || candidate.email} — Official Pest Prevention`,
                    body: this._interpolate(template.body, candidate),
                    from: campaignEmail,
                    fromName: 'Raven Resume',
                    replyTo: this._userEmail,
                    sendgridMessageId: null,
                    sendgridEvents: [],
                    calendarAccepted: false,
                    error: null,
                    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                    sentAt: null,
                    createdBy: this._userEmail,
                });
                queued++;
            } catch (err) {
                console.error(`Failed to queue calendar invite for ${candidate.email}:`, err);
                failed++;
            }
        }

        if (this._statusBar) {
            if (failed === 0) {
                this._statusBar.log(`Queued ${queued} calendar invite(s) for delivery`, 'success');
            } else {
                this._statusBar.log(`Queued ${queued}, ${failed} failed`, 'warning');
            }
        }
        if (queued === 0 && failed > 0) throw new Error(`All ${failed} invite(s) failed to queue`);
    }

    _esc(str) {
        const d = document.createElement('div');
        d.textContent = str || '';
        return d.innerHTML;
    }
}
